import { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import "leaflet/dist/leaflet.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { BottomNav } from "@/components/BottomNav";
import WelcomePage from "@/pages/WelcomePage";
import MapView from "@/pages/MapView";
import TimelineView from "@/pages/TimelineView";
import SupportView from "@/pages/SupportView";
import InfoView from "@/pages/InfoView";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Font Scale Context
export const FontScaleContext = createContext({
  fontScale: 1,
  setFontScale: () => {},
});

// Device ID for notifications (simulated)
export const getDeviceId = () => {
  let deviceId = localStorage.getItem("deviceId");
  if (!deviceId) {
    deviceId = "device_" + Math.random().toString(36).substr(2, 9);
    localStorage.setItem("deviceId", deviceId);
  }
  return deviceId;
};

// Data Context
export const DataContext = createContext({
  locations: [],
  events: [],
  contacts: [],
  crowdReports: [],
  subscriptions: [],
  loading: true,
  refreshData: () => {},
});

function App() {
  const [showWelcome, setShowWelcome] = useState(() => {
    const hasVisited = localStorage.getItem("hasVisited");
    return !hasVisited;
  });

  const [fontScale, setFontScale] = useState(() => {
    const saved = localStorage.getItem("fontScale");
    return saved ? parseFloat(saved) : 1;
  });

  const handleEnterApp = () => {
    localStorage.setItem("hasVisited", "true");
    setShowWelcome(false);
  };

  const [locations, setLocations] = useState([]);
  const [events, setEvents] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [crowdReports, setCrowdReports] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      const deviceId = getDeviceId();
      const [locRes, evtRes, conRes, crwRes, subRes] = await Promise.all([
        axios.get(`${API}/locations`),
        axios.get(`${API}/timeline-events`),
        axios.get(`${API}/contacts`),
        axios.get(`${API}/crowd-reports`),
        axios.get(`${API}/subscriptions?device_id=${deviceId}`),
      ]);
      
      setLocations(locRes.data);
      setEvents(evtRes.data);
      setContacts(conRes.data);
      setCrowdReports(crwRes.data);
      setSubscriptions(subRes.data);
    } catch (e) {
      console.error("Error fetching data:", e);
      // Try to seed data if empty
      try {
        await axios.post(`${API}/seed`);
        // Retry fetch
        const deviceId = getDeviceId();
        const [locRes, evtRes, conRes, crwRes, subRes] = await Promise.all([
          axios.get(`${API}/locations`),
          axios.get(`${API}/timeline-events`),
          axios.get(`${API}/contacts`),
          axios.get(`${API}/crowd-reports`),
          axios.get(`${API}/subscriptions?device_id=${deviceId}`),
        ]);
        
        setLocations(locRes.data);
        setEvents(evtRes.data);
        setContacts(conRes.data);
        setCrowdReports(crwRes.data);
        setSubscriptions(subRes.data);
      } catch (seedError) {
        console.error("Error seeding data:", seedError);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    document.documentElement.style.setProperty("--font-scale", fontScale);
    localStorage.setItem("fontScale", fontScale.toString());
  }, [fontScale]);

  const refreshData = () => {
    fetchData();
  };

  if (showWelcome) {
    return (
      <>
        <WelcomePage onEnter={handleEnterApp} />
        <Toaster position="top-center" richColors />
      </>
    );
  }

  return (
    <FontScaleContext.Provider value={{ fontScale, setFontScale }}>
      <DataContext.Provider
        value={{
          locations,
          events,
          contacts,
          crowdReports,
          subscriptions,
          loading,
          refreshData,
        }}
      >
        <div className="app-container">
          <BrowserRouter>
            <main className="main-content">
              <Routes>
                <Route path="/" element={<MapView />} />
                <Route path="/timeline" element={<TimelineView />} />
                <Route path="/support" element={<SupportView />} />
                <Route path="/info" element={<InfoView />} />
              </Routes>
            </main>
            <BottomNav />
          </BrowserRouter>
          <Toaster position="top-center" richColors />
        </div>
      </DataContext.Provider>
    </FontScaleContext.Provider>
  );
}

export default App;
